import { useEffect, useState } from "react";
import { Smartphone, Share, SquarePlus, EllipsisVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zap-deep text-sm font-semibold text-white">{n}</span>
    <span className="flex-1 pt-1 text-base leading-snug">{children}{Icon && <Icon className="ml-1 inline h-5 w-5 align-text-bottom text-zap-deep" />}</span>
  </li>
);

export const Guide = ({ open, onClose, ios, title = "Colocar na tela inicial", desc = "Assim o ZAP IUDP abre com um toque, como um aplicativo.", icon = "/icon-192.png" }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent data-testid="install-guide-dialog" className="max-w-sm">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{desc}</DialogDescription>
      </DialogHeader>
      <img src={icon} alt="Ícone ZAP IUDP" className="mx-auto h-20 w-20 rounded-2xl shadow" />
      <ol className="space-y-3" data-testid="install-guide-steps">
        {ios ? (<>
          <Step n={1} icon={Share}>Toque no botão Compartilhar do Safari</Step>
          <Step n={2} icon={SquarePlus}>Escolha "Adicionar à Tela de Início"</Step>
          <Step n={3}>Toque em "Adicionar"</Step>
        </>) : (<>
          <Step n={1} icon={EllipsisVertical}>Toque no menu do navegador</Step>
          <Step n={2}>Escolha "Instalar app" ou "Adicionar à tela inicial"</Step>
          <Step n={3}>Confirme em "Instalar"</Step>
        </>)}
      </ol>
      <button data-testid="install-guide-close" onClick={onClose} className="mt-2 h-12 w-full rounded-full bg-zap-deep text-base font-semibold text-white">Entendi</button>
    </DialogContent>
  </Dialog>
);

export const InstallApp = () => {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    if (!prompt) { setGuide(true); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  return (
    <>
      <button data-testid="install-app-button" onClick={install} aria-label="Instalar na tela inicial"
        className="flex h-12 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold leading-none text-white transition-colors hover:bg-white/10 sm:h-11 sm:w-auto sm:flex-row sm:gap-2 sm:rounded-full sm:border sm:border-[#C9A24A]/60 sm:px-3 sm:text-sm">
        <Smartphone className="h-5 w-5 text-[#C9A24A]" />
        <span>Instalar</span>
      </button>
      <Guide open={guide} onClose={() => setGuide(false)} ios={isIOS()} />
    </>
  );
};
